<template>
    <div id="openrouter-channels">
        <AppBreadCrumb
            :paths="[
                { title: $t('message.integrations'), route: 'integrations' },
                { title: providerInfo.name },
            ]"
            :select="true"
        />
        <div class="page-content">
            <div class="provider-header">
                <div class="provider-logo">
                    <img :src="providerInfo.logo" :alt="providerInfo.name" />
                </div>
                <div class="provider-info">
                    <h1>{{ providerInfo.name }}</h1>
                    <p>{{ providerInfo.description }}</p>
                </div>
            </div>

            <div class="channels-section">
                <div class="section-header">
                    <h2>{{ $t("message.channels") || "Kanallar" }}</h2>
                    <a-button type="primary" @click="showCreateModal = true">
                        <span class="material-icons">add</span>
                        {{ $t("whatsappChannel.addChannel") || "Kanal Ekle" }}
                    </a-button>
                </div>

                <div class="channels-grid" v-if="providerChannels.length > 0">
                    <div
                        class="channel-card"
                        v-for="channel in providerChannels"
                        :key="channel._id"
                        @click="editChannel(channel)"
                    >
                        <div class="card-header">
                            <div class="channel-icon">
                                <img :src="providerInfo.logo" :alt="providerInfo.name" />
                            </div>
                            <div class="channel-status" :class="{ enabled: channel.enabled }">
                                {{ channel.enabled ? $t("message.active") : $t("message.inactive") }}
                            </div>
                        </div>
                        <div class="card-body">
                            <div class="channel-name">{{ channel.name }}</div>
                            <div class="channel-meta">
                                <span class="key-mode">
                                    <span class="material-icons">vpn_key</span>
                                    {{ channel.payload.keyMode === 'byok' ? 'BYOK' : 'Supsis' }}
                                </span>
                            </div>
                        </div>
                        <div class="card-actions">
                            <a-dropdown :trigger="['click']" placement="bottomRight">
                                <a-button size="small" @click.stop>
                                    <span class="material-icons">more_vert</span>
                                </a-button>
                                <template #overlay>
                                    <a-menu>
                                        <a-menu-item @click.stop="copyChannelId(channel._id)">
                                            <span class="material-icons">content_copy</span>
                                            {{ $t("message.copyChannelID") || "Channel ID Kopyala" }}
                                        </a-menu-item>
                                        <a-menu-item @click.stop="confirmDelete(channel)">
                                            <span class="material-icons" style="color: #f5222d">delete</span>
                                            <span style="color: #f5222d">{{ $t("chatbotv2.delete") }}</span>
                                        </a-menu-item>
                                    </a-menu>
                                </template>
                            </a-dropdown>
                        </div>
                    </div>
                </div>

                <div class="empty-state" v-else>
                    <div class="empty-icon">
                        <span class="material-icons">cloud_off</span>
                    </div>
                    <h3>{{ $t("message.noChannels") || "Henüz kanal yok" }}</h3>
                    <p>{{ $t("message.createFirstChannel") || "İlk kanalınızı oluşturmak için 'Kanal Ekle' butonuna tıklayın." }}</p>
                    <a-button type="primary" @click="showCreateModal = true">
                        <span class="material-icons">add</span>
                        {{ $t("whatsappChannel.addChannel") || "Kanal Ekle" }}
                    </a-button>
                </div>
            </div>
        </div>

        <!-- Create Channel Modal -->
        <a-modal
            v-model="showCreateModal"
            :title="$t('message.createChannel') || 'Kanal Oluştur'"
            :footer="null"
            width="500px"
        >
            <CreateOpenRouterChannelForm
                :provider="provider"
                @ok="handleChannelCreated"
                @cancel="showCreateModal = false"
            />
        </a-modal>

        <!-- Delete Confirmation Modal -->
        <a-modal
            v-model="deleteModal"
            :title="$t('message.deleteConfirm') || 'Kanalı Sil'"
            @cancel="deleteModal = false"
        >
            <p>{{ $t("message.deleteChannelConfirm") || "Bu kanalı silmek istediğinize emin misiniz?" }}</p>
            <template #footer>
                <a-button @click="deleteModal = false">{{ $t("message.cancel") }}</a-button>
                <a-button type="danger" @click="handleDeleteChannel">{{ $t("chatbotv2.delete") }}</a-button>
            </template>
        </a-modal>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import _ from "lodash";
import AppBreadCrumb from "../../../../components/common/AppBreadCrumb.vue";
import CreateOpenRouterChannelForm from "./CreateOpenRouterChannelForm.vue";

const PROVIDER_INFO = {
    openrouter: {
        name: "OpenRouter",
        description: "Tüm LLM modellerine tek API ile erişim sağlayın. OpenRouter, birçok farklı AI sağlayıcısını tek bir API altında birleştirir.",
        logo: "https://static.supsis.live/common/ai/openrouter.svg",
    },
    openai: {
        name: "OpenAI",
        description: "GPT-4, GPT-4o, ChatGPT ve diğer OpenAI modellerine erişim sağlayın.",
        logo: "https://static.supsis.live/common/ai/openai.svg",
    },
    google: {
        name: "Google AI Studio",
        description: "Google Gemini modelleri ile güçlü AI uygulamaları oluşturun.",
        logo: "https://static.supsis.live/common/ai/google.svg",
    },
    anthropic: {
        name: "Anthropic",
        description: "Claude 3.5 Sonnet, Claude 3 Opus ve diğer Claude modellerine erişim.",
        logo: "https://static.supsis.live/common/ai/anthropic.svg",
    },
    mistral: {
        name: "Mistral",
        description: "Mistral Large ve diğer Mistral AI modellerini kullanın.",
        logo: "https://static.supsis.live/common/ai/mistral.webp",
    },
    azure: {
        name: "Microsoft Azure",
        description: "Azure OpenAI servisleri ile kurumsal düzeyde AI çözümleri.",
        logo: "https://static.supsis.live/common/ai/microsoft.svg",
    },
    bedrock: {
        name: "Amazon Bedrock",
        description: "AWS'nin yönetilen LLM platformu ile entegrasyon.",
        logo: "https://static.supsis.live/common/ai/aws.svg",
    },
    cohere: {
        name: "Cohere",
        description: "Command R+ ve diğer Cohere NLP modellerine erişim.",
        logo: "https://static.supsis.live/common/ai/cohere.svg",
    },
    xai: {
        name: "xAI",
        description: "Grok modelleri ile X (Twitter) destekli AI çözümleri.",
        logo: "https://static.supsis.live/common/ai/xai.svg",
    },
    deepinfra: {
        name: "DeepInfra",
        description: "Hızlı ve uygun maliyetli LLM inference altyapısı.",
        logo: "https://static.supsis.live/common/ai/deepinfra.webp",
    },
    together: {
        name: "Together",
        description: "Açık kaynak LLM modelleri ile esnek AI çözümleri.",
        logo: "https://static.supsis.live/common/ai/togetherai.svg",
    },
    fireworks: {
        name: "Fireworks",
        description: "Hızlı ve optimize edilmiş LLM inference servisi.",
        logo: "https://static.supsis.live/common/ai/fireworks.svg",
    },
    siliconflow: {
        name: "SiliconFlow",
        description: "AI model hosting ve inference platformu.",
        logo: "https://static.supsis.live/common/ai/siliconflow.png",
    },
    parasail: {
        name: "Parasail",
        description: "LLM API servisi ve model erişimi.",
        logo: "https://static.supsis.live/common/ai/parasail.svg",
    },
    nebius: {
        name: "Nebius",
        description: "Cloud AI altyapısı ve model servisleri.",
        logo: "https://static.supsis.live/common/ai/nebius.svg",
    },
    alibaba: {
        name: "Alibaba Cloud",
        description: "Qwen ve diğer Alibaba AI modellerine erişim.",
        logo: "https://static.supsis.live/common/ai/alibaba.svg",
    },
    xiaomi: {
        name: "Xiaomi",
        description: "MiLM ve Xiaomi AI modellerine erişim.",
        logo: "https://static.supsis.live/common/ai/xiaomi.svg",
    },
    sambanova: {
        name: "SambaNova",
        description: "AI donanım ve yazılım çözümleri.",
        logo: "https://static.supsis.live/common/ai/sambanova.webp",
    },
    moonshot: {
        name: "Moonshot AI",
        description: "Kimi ve diğer Moonshot modellerine erişim.",
        logo: "https://static.supsis.live/common/ai/moonshot.svg",
    },
    minimax: {
        name: "Minimax",
        description: "Abab ve diğer Minimax AI modellerine erişim.",
        logo: "https://static.supsis.live/common/ai/minimax.svg",
    },
    nvidia: {
        name: "NVIDIA",
        description: "NVIDIA NIM ve GPU hızlandırmalı AI modelleri.",
        logo: "https://static.supsis.live/common/ai/nvidia.svg",
    },
};

export default {
    name: "OpenRouterChannels",
    components: {
        AppBreadCrumb,
        CreateOpenRouterChannelForm,
    },
    data() {
        return {
            showCreateModal: false,
            deleteModal: false,
            channelToDelete: null,
        };
    },
    computed: {
        ...mapState({
            channels: (state) => state.channel.channels,
        }),
        provider() {
            return this.$route.params.provider || "openrouter";
        },
        providerInfo() {
            return PROVIDER_INFO[this.provider] || PROVIDER_INFO.openrouter;
        },
        providerChannels() {
            return this.channels.filter(
                (c) => c.type === "openrouter" && _.get(c, "payload.provider") === this.provider
            );
        },
    },
    methods: {
        ...mapActions({
            listChannels: "channel/listChannels",
            deleteChannel: "channel/deleteChannel",
        }),
        editChannel(channel) {
            this.$router.push(`/integrations/openrouter/${this.provider}/${channel._id}/detail`);
        },
        copyChannelId(channelId) {
            navigator.clipboard.writeText(channelId).then(() => {
                this.$message.success(this.$t("message.copiedToClipboard") || "Kopyalandı");
            });
        },
        confirmDelete(channel) {
            this.channelToDelete = channel;
            this.deleteModal = true;
        },
        handleDeleteChannel() {
            if (this.channelToDelete) {
                this.deleteChannel(this.channelToDelete._id).then(() => {
                    this.$message.success(this.$t("message.channelDeletedSuccessfully") || "Kanal silindi");
                    this.listChannels();
                }).catch(() => {
                    this.$message.error(this.$t("message.failedToDeleteChannel") || "Kanal silinemedi");
                });
            }
            this.deleteModal = false;
            this.channelToDelete = null;
        },
        handleChannelCreated() {
            this.showCreateModal = false;
            this.listChannels();
        },
    },
    mounted() {
        this.listChannels();
    },
};
</script>

<style lang="scss" scoped>
#openrouter-channels {
    width: 100%;
    min-height: calc(100vh - 50px);
    background: var(--color-bg-default);

    .page-content {
        padding: 20px 30px;
    }

    .provider-header {
        display: flex;
        align-items: center;
        gap: 20px;
        padding: 30px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border-radius: 12px;
        margin-bottom: 30px;

        .provider-logo {
            width: 80px;
            height: 80px;
            background: #fff;
            border-radius: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 15px;

            img {
                width: 100%;
                height: 100%;
                object-fit: contain;
            }
        }

        .provider-info {
            color: #fff;

            h1 {
                font-size: 28px;
                font-weight: 700;
                margin: 0 0 8px 0;
                color: #fff;
            }

            p {
                font-size: 14px;
                margin: 0;
                opacity: 0.9;
            }
        }
    }

    .channels-section {
        .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;

            h2 {
                font-size: 20px;
                font-weight: 600;
                margin: 0;
            }

            .ant-btn {
                display: flex;
                align-items: center;
                gap: 5px;

                .material-icons {
                    font-size: 18px;
                }
            }
        }

        .channels-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
            gap: 20px;
        }

        .channel-card {
            background: #fff;
            border: 1px solid var(--color-border-default);
            border-radius: 12px;
            padding: 20px;
            cursor: pointer;
            transition: all 0.2s ease;
            position: relative;

            &:hover {
                border-color: var(--color-primary);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                transform: translateY(-2px);
            }

            .card-header {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                margin-bottom: 15px;

                .channel-icon {
                    width: 48px;
                    height: 48px;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 8px;

                    img {
                        width: 100%;
                        height: 100%;
                        object-fit: contain;
                    }
                }

                .channel-status {
                    font-size: 12px;
                    padding: 4px 10px;
                    border-radius: 20px;
                    background: #fef2f2;
                    color: #dc2626;

                    &.enabled {
                        background: #f0fdf4;
                        color: #16a34a;
                    }
                }
            }

            .card-body {
                .channel-name {
                    font-size: 16px;
                    font-weight: 600;
                    margin-bottom: 8px;
                }

                .channel-meta {
                    display: flex;
                    gap: 15px;
                    color: var(--color-text-muted);
                    font-size: 13px;

                    .key-mode {
                        display: flex;
                        align-items: center;
                        gap: 4px;

                        .material-icons {
                            font-size: 16px;
                        }
                    }
                }
            }

            .card-actions {
                position: absolute;
                top: 15px;
                right: 15px;
                opacity: 0;
                transition: opacity 0.2s ease;

                .ant-btn {
                    padding: 4px;
                    height: auto;
                    border: none;
                    background: transparent;

                    .material-icons {
                        font-size: 20px;
                    }
                }
            }

            &:hover .card-actions {
                opacity: 1;
            }
        }

        .empty-state {
            text-align: center;
            padding: 60px 20px;
            background: #fff;
            border: 1px dashed var(--color-border-default);
            border-radius: 12px;

            .empty-icon {
                width: 80px;
                height: 80px;
                background: #f8fafc;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                margin: 0 auto 20px;

                .material-icons {
                    font-size: 40px;
                    color: var(--color-text-muted);
                }
            }

            h3 {
                font-size: 18px;
                font-weight: 600;
                margin-bottom: 8px;
            }

            p {
                color: var(--color-text-muted);
                margin-bottom: 20px;
            }

            .ant-btn {
                display: inline-flex;
                align-items: center;
                gap: 5px;
            }
        }
    }
}
</style>
